/**
 * Plyr provider embed (YouTube / Vimeo) caption helpers.
 *
 * Why prototype patching? Plyr’s built-in `toggleCaptions` is broken for YouTube because
 * `captions.setup()` is skipped, leaving `this.captions.languages` undefined — the next call
 * throws `this.captions.languages is not iterable`. Overriding at the instance level proved
 * fragile across `source = …` rebuilds and timing races; patching the prototype once applies
 * to every Plyr instance for the lifetime of the page.
 *
 * Note: auto-generated-only YouTube captions may still not honor "force on" in all cases
 * (YouTube limitation).
 */

import Plyr from 'plyr';

export function getPreferredCaptionLanguage() {
  try {
    const langs = navigator.languages?.length
      ? navigator.languages
      : [navigator.language || 'en'];
    const first = langs.find(Boolean) || 'en';
    const code = String(first).split('-')[0].toLowerCase();
    return /^[a-z]{2}$/.test(code) ? code : 'en';
  } catch {
    return 'en';
  }
}

function getPreferredCaptionLanguagesArray() {
  const raw = navigator.languages?.length ? navigator.languages : [navigator.language || 'en'];
  const codes = raw.map(l => String(l).split('-')[0].toLowerCase()).filter(Boolean);
  const out = [...new Set(codes)];
  return out.length ? out : ['en'];
}

/**
 * @param {Record<string, unknown>} baseOptions
 * @param {Element | null | undefined} element
 */
export function withProviderEmbedCaptions(baseOptions, element) {
  const provider = element?.getAttribute?.('data-plyr-provider') || '';
  if (provider !== 'youtube' && provider !== 'vimeo') {
    return baseOptions;
  }

  const lang = getPreferredCaptionLanguage();
  return {
    ...baseOptions,
    captions: {
      ...(baseOptions.captions || {}),
      active: true,
      language: lang,
      update: true,
    },
  };
}

/**
 * Workaround: Plyr has had cases where cc_load_policy doesn't apply until captions.active is set after init.
 */
export function nudgeYouTubeCaptionsIfNeeded(player) {
  if (!player?.isYouTube) return;

  const run = () => {
    try {
      if (player.captions) {
        player.captions.active = true;
      }
    } catch {
      // ignore
    }
  };

  try {
    player.on('ready', run);
  } catch {
    // ignore
  }
  queueMicrotask(run);
}

/**
 * Ensure `captions.languages` is an array so Plyr never throws on `[...this.captions.languages]`.
 * YouTube skips captions.setup entirely; HTML5 can also end up without `languages` after source
 * rebuilds.
 */
export function shimCaptionsLanguagesIfNeeded(player) {
  const caps = player?.captions;
  if (!caps) return false;

  if (Array.isArray(caps.languages)) return false;

  caps.languages = getPreferredCaptionLanguagesArray();
  if (typeof caps.toggled !== 'boolean') {
    caps.toggled = false;
  }
  return true;
}

function doYouTubeCaptions(embed, turningOn) {
  const lang = getPreferredCaptionLanguage();
  if (!embed || typeof embed.loadModule !== 'function' || typeof embed.setOption !== 'function') {
    return;
  }
  if (turningOn) {
    try { embed.loadModule('captions'); } catch { /* ignore */ }
    try { embed.setOption('captions', 'track', { languageCode: lang }); } catch { /* ignore */ }
    try { embed.setOption('captions', 'reload', true); } catch { /* ignore */ }
  } else {
    try { embed.setOption('captions', 'track', {}); } catch { /* ignore */ }
    try { embed.unloadModule?.('captions'); } catch { /* ignore */ }
    try { embed.unloadModule?.('cc'); } catch { /* ignore */ }
  }
}

/**
 * Patch `Plyr.prototype.toggleCaptions` once so every instance is safe:
 *  - YouTube → drive captions via the YT IFrame API, update pressed / active-class / storage.
 *  - HTML5 / other → shim `captions.languages` and call the original `toggleCaptions` in a
 *    try/catch so a bad Plyr path cannot crash the page.
 */
export function installPlyrPrototypeCaptionsFix() {
  const proto = Plyr?.prototype;
  if (!proto || typeof proto.toggleCaptions !== 'function') return;
  if (proto.__pathstonePatchedToggleCaptions) return;
  proto.__pathstonePatchedToggleCaptions = true;

  const native = proto.toggleCaptions;

  proto.toggleCaptions = function patchedToggleCaptions(input) {
    if (this.isYouTube) {
      const captionsBtn = this.elements?.buttons?.captions;
      const next =
        typeof input === 'boolean' ? input : !captionsBtn?.pressed;

      try {
        doYouTubeCaptions(this.embed, next);
      } catch {
        // YouTube IFrame API variance; ignore
      }

      if (captionsBtn) captionsBtn.pressed = next;

      const container = this.elements?.container;
      if (container) {
        container.classList.toggle('plyr--captions-active', next);
        container.classList.add('plyr--captions-enabled');
      }

      try {
        this.captions.toggled = next;
        this.captions.active = next;
        if (this.storage?.set) this.storage.set({ captions: next });
      } catch {
        // ignore
      }
      return;
    }

    shimCaptionsLanguagesIfNeeded(this);
    try {
      return native.call(this, input);
    } catch {
      // Guard against Plyr's HTML5 captions path throwing on malformed state.
    }
  };
}

/**
 * Per-instance YouTube hook:
 *  - Make sure the CC control is visible (Plyr hides it unless `plyr--captions-enabled`).
 *  - If captions are on by default (via `captions.active`), sync the button pressed state
 *    and `plyr--captions-active` class so the CC control doesn't look off / "disabled" —
 *    otherwise the user's first click appears to "reload" captions by toggling off→on.
 *
 * No longer touches `toggleCaptions` — the prototype patch does that.
 */
export function wireYouTubeEmbedCaptionToggle(player) {
  if (!player?.isYouTube) return;

  const apply = () => {
    const container = player.elements?.container;
    if (container) {
      container.classList.add('plyr--captions-enabled');
    }
    shimCaptionsLanguagesIfNeeded(player);

    const captionsBtn = player.elements?.buttons?.captions;
    if (captionsBtn) {
      // Plyr may mark the CC button disabled for YouTube because there are no HTML5 text tracks.
      if (captionsBtn.disabled) captionsBtn.disabled = false;
      captionsBtn.removeAttribute('aria-disabled');
    }

    const active = Boolean(player.captions?.active);
    if (active) {
      if (captionsBtn) captionsBtn.pressed = true;
      if (container) container.classList.add('plyr--captions-active');
      try {
        player.captions.toggled = true;
      } catch {
        // ignore
      }
    }
  };

  try {
    player.on('ready', apply);
  } catch {
    // ignore
  }
  queueMicrotask(apply);
  setTimeout(apply, 100);
  setTimeout(apply, 500);
}
